import ESputnik from '@/utils/Analytics/ESputnik/ESputnik';
import { usePersonalStore } from '@/storePinia/stores/personal';
import { useBasketStore } from '@/storePinia/stores/basket';
import { TikTokPixel } from '@/utils/Analytics/TikTokPixel';
import { IProductDetail } from '@/storePinia/stores/product/interfaces/IProductDetail';
import { ICatalogCollection } from '@/storePinia/stores/catalog/interfaces/ICatalog';
import { ISearchResponse } from '@components/Search/ISearch';
import { IOrderStore } from '@/storePinia/stores/thanks/interfaces/IOrder';
import GoogleAnalytics4 from './GoogleAnalytics4';

interface IGeneralData {
  currency: string;
  // eslint-disable-next-line camelcase
  transaction_id: string;
  shipping: string;
  value: string;
  affiliation: string;
}

type IProductWithCount = IProductDetail & { count: number };

export default class Analytics {
  static viewDetailProduct(product: IProductDetail) {
    const dataLayerProduct = product?.dataLayerProduct;

    if (dataLayerProduct) {
      GoogleAnalytics4.viewItem({ ...dataLayerProduct, group_id: product.id });
    }

    ESputnik.productPage(product);

    const tiktok = TikTokPixel.init();
    tiktok.viewProduct(product);
  }

  static clickOnProduct(product: IProductDetail, pageNumber: number): undefined {
    type IDataLayerProduct = IProductDetail['dataLayerProduct'] & {
      // eslint-disable-next-line camelcase
      item_list_id?: string;
    };

    const dataLayerProduct: IDataLayerProduct = { ...product?.dataLayerProduct };

    if (!Object.keys(dataLayerProduct).length) {
      console.warn('dataLayerProduct is empty', product);
      return undefined;
    }

    if (pageNumber) {
      dataLayerProduct.item_list_id = `list ${pageNumber}`;
    }

    if (dataLayerProduct) {
      GoogleAnalytics4.selectItem(dataLayerProduct);
    }

    return undefined;
  }

  static buyOneClick(items: IProductDetail[], generalData: IGeneralData) {
    if (items) {
      GoogleAnalytics4.purchase(items, generalData);
    }
  }

  /** Catalog Events * */
  static viewCatalog(items: IProductDetail[], collection: ICatalogCollection) {
    if (items?.length) {
      GoogleAnalytics4.viewItemList(
        items.map((el) => el.dataLayerProduct),
        collection?.name
      );

      // if (usePersonalStore().isAuthorized) {
      if (collection?.name) {
        ESputnik.categoryPage(collection.name);
      }

      // }
    }
  }
  /** End Catalog Events * */

  /** Checkout events * */
  static viewCheckout(items: IProductWithCount[]) {
    const products = items.map((el) => {
      const quantity = Number(el.count ?? 1);
      return { ...el.dataLayerProduct, quantity };
    });

    if (products) {
      GoogleAnalytics4.beginCheckout(products);
    }

    const tiktok = TikTokPixel.init();
    tiktok.initiateCheckout(items);
  }

  static confirmCheckout(thanksData: IOrderStore, generalData: IGeneralData) {
    const dataLayerProducts = thanksData?.order?.dataLayer?.products;
    const items = thanksData?.order?.items;
    // console.log(dataLayerProducts);
    if (dataLayerProducts) {
      GoogleAnalytics4.purchase(Object.values(dataLayerProducts), generalData);
    }
    if (items) {
      ESputnik.purchasedItems(items, thanksData.order);
    }

    if (items) {
      const tiktok = TikTokPixel.init();
      tiktok.purchase(generalData, items);
    }
  }
  /** Checkout events end * */

  /** Basket events * */
  static viewBasket(items: IProductDetail[], generalData: IGeneralData) {
    if (items?.length) {
      GoogleAnalytics4.viewCart(
        items.map((el) => el.dataLayerProduct),
        generalData
      );
    }
  }

  static addToBasket(product: IProductDetail) {
    const dataLayerProduct = product?.dataLayerProduct;

    if (dataLayerProduct) {
      GoogleAnalytics4.addToCart({ ...dataLayerProduct, quantity: 1, group_id: product.id });
    }

    ESputnik.statusCart(useBasketStore());

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'AddToCart', {
        content_ids: [product.id],
        content_type: 'product',
        value: product.price,
        currency: 'UAH',
      });
    }

    const tiktok = TikTokPixel.init();
    tiktok.addToCart(product);
  }

  static removeFromBasket(product: IProductWithCount) {
    const dataLayerProduct = product?.dataLayerProduct;

    if (dataLayerProduct) {
      const quantity = Number(product.count ?? 1);

      GoogleAnalytics4.removeFromCart({
        ...dataLayerProduct,
        quantity,
      });

      ESputnik.statusCart(useBasketStore());
    }
  }

  static updatedBasket() {
    ESputnik.statusCart(useBasketStore());
  }
  /** End Basket events * */

  /** User events * */
  // eslint-disable-next-line camelcase
  static signIn(customerData: { user_phone: string; user_name?: string; user_email: string }) {
    ESputnik.customerData(customerData);
  }

  /** End User events * */

  /** Search engine * */
  static search(searchData: ISearchResponse) {
    if (
      usePersonalStore().isAuthorized &&
      searchData.query &&
      !searchData?.products?.items?.length
    ) {
      ESputnik.searchRequest(searchData.query);
    }
  }
  /** End Search engine * */

  /** wishlist * */
  static addToWishlist(product: IProductDetail) {
    if (usePersonalStore().isAuthorized) {
      ESputnik.addToWishlist(product);

      const tiktok = TikTokPixel.init();
      tiktok.addToWishlist(product);
    }
  }
  /** End wishlist * */
}
